/**
 * Block tree → markdown string.
 *
 * Conventions:
 *   - Headings: `# / ## / ### / ####`
 *   - Bold: `**...**`, Italic: `*...*` (single asterisk; tighter than `_..._` and avoids
 *     accidental snake_case underline matches inside extracted code-like text)
 *   - Lists: `-` for unordered, `1.`/`a.`/the original marker for ordered
 *   - Tables: GFM pipe tables when shape is regular; falls back to fenced text otherwise
 *   - Blank line between blocks
 *   - PageBreak: HTML comment `<!-- page N -->` (preserved for downstream reference,
 *     does not visually render in most viewers)
 */
export function renderBlocks(blocks) {
    const out = [];
    for (const block of blocks) {
        const md = renderBlock(block);
        if (md.length > 0)
            out.push(md);
    }
    return out.join('\n\n').trim() + '\n';
}
function renderBlock(block) {
    switch (block.kind) {
        case 'heading':
            return '#'.repeat(block.level) + ' ' + renderRuns(block.runs);
        case 'paragraph':
            return renderRuns(block.runs);
        case 'list':
            return renderList(block.ordered, block.items);
        case 'table':
            return renderTable(block.rows, block.hasHeader);
        case 'pageBreak':
            return `<!-- page ${block.page} -->`;
    }
}
function renderRuns(runs) {
    return runs
        .map((r) => wrapStyle(escapeMarkdown(r.text), r.bold, r.italic))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}
function wrapStyle(text, bold, italic) {
    if (text.length === 0)
        return text;
    let s = text;
    if (italic && bold)
        s = `***${s}***`;
    else if (bold)
        s = `**${s}**`;
    else if (italic)
        s = `*${s}*`;
    return s;
}
/** Escape characters that have markdown meaning. Conservative — only the common pitfalls. */
function escapeMarkdown(text) {
    // Escape backticks (would open inline code) and pipe (would break tables).
    return text.replace(/([`|])/g, '\\$1');
}
function renderList(ordered, items) {
    const lines = [];
    for (const item of items) {
        const indent = '  '.repeat(item.depth);
        const marker = ordered ? (item.marker ?? '1.') : '-';
        const text = renderRuns(item.runs);
        lines.push(`${indent}${marker} ${text}`);
    }
    return lines.join('\n');
}
function renderTable(rows, hasHeader) {
    if (rows.length === 0)
        return '';
    const colCount = Math.max(...rows.map((r) => r.length));
    if (colCount < 2)
        return '';
    const padRow = (row) => {
        const cells = row.slice();
        while (cells.length < colCount)
            cells.push([]);
        return cells.map((cell) => renderRuns(cell).replace(/\n/g, ' '));
    };
    const lines = [];
    if (hasHeader) {
        lines.push('| ' + padRow(rows[0]).join(' | ') + ' |');
        lines.push('| ' + new Array(colCount).fill('---').join(' | ') + ' |');
        for (let i = 1; i < rows.length; i++) {
            lines.push('| ' + padRow(rows[i]).join(' | ') + ' |');
        }
    }
    else {
        // Synthesize a blank header row so GFM renders the table.
        lines.push('| ' + new Array(colCount).fill(' ').join(' | ') + ' |');
        lines.push('| ' + new Array(colCount).fill('---').join(' | ') + ' |');
        for (const row of rows) {
            lines.push('| ' + padRow(row).join(' | ') + ' |');
        }
    }
    return lines.join('\n');
}
//# sourceMappingURL=render.js.map